import React, { useMemo, useState } from 'react';
import { Text } from '../text';
import {
  ClockIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { getEmisorNombre } from '../../utils/calculations';
import {
  aggregateProveedores,
  computeConvenioTotals,
  computeEmittedFianzas,
  getFianzaStatus,
} from '../../utils/convenioCompliance';
import SearchableListbox from '../SearchableListbox';
import SectionCard from './SectionCard';

const formatMonto = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

// Full lifecycle vocabulary — same codes getFianzaStatus emits. Cancelada is
// the HEALTHY end of a fianza (the contrato concluded and the línea de
// afianzamiento was released); vencida is the one that demands action.
const LIFECYCLE_ESTADOS = [
  { code: 'vigente', label: 'Vigentes', dot: 'bg-green-500', sub: 'cobertura activa' },
  { code: 'pendiente', label: 'Por iniciar', dot: 'bg-amber-400', sub: 'vigencia futura' },
  { code: 'vencida', label: 'Vencidas', dot: 'bg-red-500', sub: 'requiere cancelación', alert: true },
  { code: 'cancelada', label: 'Canceladas', dot: 'bg-zinc-400', sub: 'línea liberada' },
  { code: 'sustituida', label: 'Sustituidas', dot: 'bg-cian-digital', sub: 'reemplazadas' },
  { code: 'anulada', label: 'Anuladas', dot: 'bg-zinc-600', sub: 'sin efecto' },
];

const ConvenioResumen = ({
  contracts = [],
  beneficiarios = [],
  beneficiariosById = {},
  proyectosById = {},
  afianzadorasById = {},
  onProveedorClick,
  onFianzaClick,
}) => {
  const now = Date.now();
  const [selectedBeneficiarioId, setSelectedBeneficiarioId] = useState('all');

  // Limit beneficiario options to those that have accessible contracts
  const beneficiarioOptions = useMemo(() => {
    const present = new Set();
    contracts.forEach((c) => { if (c.beneficiarioId) present.add(c.beneficiarioId); });
    const opts = beneficiarios
      .filter((b) => present.has(b.id))
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
      .map((b) => ({ value: b.id, label: b.nombre || 'Sin nombre' }));
    return [{ value: 'all', label: 'Todos los beneficiarios' }, ...opts];
  }, [contracts, beneficiarios]);

  const filteredContracts = useMemo(() => {
    if (selectedBeneficiarioId === 'all') return contracts;
    return contracts.filter((c) => c.beneficiarioId === selectedBeneficiarioId);
  }, [contracts, selectedBeneficiarioId]);

  const proveedoresAgg = useMemo(
    () => aggregateProveedores(filteredContracts, beneficiariosById, proyectosById),
    [filteredContracts, beneficiariosById, proyectosById]
  );
  const totals = useMemo(() => computeConvenioTotals(proveedoresAgg), [proveedoresAgg]);
  const allFianzas = useMemo(
    () => computeEmittedFianzas(filteredContracts, beneficiariosById, proyectosById),
    [filteredContracts, beneficiariosById, proyectosById]
  );

  const { upcomingExpirations, expBuckets, afianzadoraDistribution, montoVigenteTotal } = useMemo(() => {
    const day30 = now + 30 * 86400000;
    const day60 = now + 60 * 86400000;
    const day90 = now + 90 * 86400000;

    const upcoming = allFianzas
      .map((f) => {
        const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
        return { ...f, _finMs: fin };
      })
      .filter((f) => f._finMs > now && f._finMs <= day90)
      .sort((a, b) => a._finMs - b._finMs);

    const buckets = {
      d30: upcoming.filter((f) => f._finMs <= day30).length,
      d60: upcoming.filter((f) => f._finMs > day30 && f._finMs <= day60).length,
      d90: upcoming.filter((f) => f._finMs > day60 && f._finMs <= day90).length,
    };

    const afMap = new Map();
    let montoVigente = 0;
    allFianzas.forEach((f) => {
      const status = getFianzaStatus(f, now);
      if (status.label !== 'Vigente') return;
      let emisorNombre = getEmisorNombre(f);
      if (!emisorNombre && f.emisor && afianzadorasById[f.emisor]) {
        emisorNombre = afianzadorasById[f.emisor].nombre;
      }
      const nombre = (emisorNombre || 'Sin afianzadora').trim();
      const key = nombre.toLowerCase();
      if (!afMap.has(key)) {
        const cat = Object.values(afianzadorasById).find(
          (a) => a.nombre && a.nombre.trim().toLowerCase() === key
        );
        afMap.set(key, { nombre, logo: cat?.logo || null, count: 0, monto: 0 });
      }
      const entry = afMap.get(key);
      entry.count++;
      const monto = Number(f.montoAfianzado) || 0;
      entry.monto += monto;
      montoVigente += monto;
    });

    return {
      upcomingExpirations: upcoming,
      expBuckets: buckets,
      afianzadoraDistribution: Array.from(afMap.values()).sort((a, b) => b.monto - a.monto),
      montoVigenteTotal: montoVigente,
    };
  }, [allFianzas, afianzadorasById, now]);

  // Every emitted fianza bucketed by its lifecycle estado — the full story,
  // terminal states included, not just the live-exposure slice.
  const lifecycle = useMemo(() => {
    const acc = Object.fromEntries(
      LIFECYCLE_ESTADOS.map((e) => [e.code, { count: 0, monto: 0 }])
    );
    allFianzas.forEach((f) => {
      const code = getFianzaStatus(f, now).code;
      if (!acc[code]) return;
      acc[code].count++;
      acc[code].monto += Number(f.montoAfianzado) || 0;
    });
    const total = Object.values(acc).reduce((s, e) => s + e.count, 0);
    return { acc, total };
  }, [allFianzas, now]);

  // Attention = something the broker can act on today. A futura (vicios ocultos
  // that can't be emitted until the contrato ends) is not actionable, so it
  // must not drag a healthy proveedor onto this list — hence porEmitir, not
  // pendiente, which counts futuras too.
  const topProveedores = proveedoresAgg
    .filter((p) => p.counts.overdue > 0 || p.counts.dueSoon > 0 || p.counts.porEmitir > 0 || p.counts.vencida > 0)
    .slice(0, 5);

  const selectedBeneficiarioLabel = selectedBeneficiarioId === 'all'
    ? null
    : beneficiariosById[selectedBeneficiarioId]?.nombre || null;

  return (
    <div className="space-y-4">
      {/* Beneficiario selector */}
      {beneficiarioOptions.length > 2 && (
        <div className="bg-white rounded-lg border border-azul-surelia/15 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">
              Beneficiario:
            </label>
            <SearchableListbox
              value={selectedBeneficiarioId}
              onChange={setSelectedBeneficiarioId}
              options={beneficiarioOptions}
              className="min-w-[260px] max-w-[360px]"
              placeholder="Selecciona beneficiario"
              searchPlaceholder="Buscar beneficiario..."
              popupClassName="w-[360px]"
            />
            {selectedBeneficiarioLabel && (
              <Text className="text-xs text-zinc-500 truncate">
                Mostrando datos de {selectedBeneficiarioLabel}
              </Text>
            )}
          </div>
        </div>
      )}

      {/* 1. Salud del Convenio */}
      <SectionCard
        icon={ShieldCheckIcon}
        title={`Salud ${selectedBeneficiarioId === 'all' ? 'del Convenio' : 'del Beneficiario'}`}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div
            className="bg-green-50 border border-green-100 rounded-lg p-3"
            title="Suma del monto afianzado de las fianzas VIGENTES. Excluye vencidas, canceladas y las que aún no se emiten."
          >
            <Text className="text-xs text-green-700 uppercase tracking-wide">Afianzado Vigente</Text>
            <p className="text-lg font-bold text-green-700">{formatMonto(montoVigenteTotal)}</p>
            <Text className="text-[10px] text-green-700/70 mt-0.5">monto afianzado en vigor</Text>
          </div>
          <div
            className="bg-amber-50 border border-amber-100 rounded-lg p-3"
            title="Monto por afianzar de las fianzas requeridas que todavía no se emiten."
          >
            <Text className="text-xs text-amber-700 uppercase tracking-wide">Por Emitir</Text>
            <p className="text-lg font-bold text-amber-700">{formatMonto(totals.montoPorEmitir)}</p>
            {totals.montoFutura > 0 && (
              <Text className="text-[10px] text-zinc-500 mt-0.5">
                + {formatMonto(totals.montoFutura)} futuras
              </Text>
            )}
          </div>
          <div
            className="bg-red-50 border border-red-100 rounded-lg p-3"
            title="Monto afianzado de las fianzas cuya vigencia ya terminó y siguen sin cancelarse."
          >
            <Text className="text-xs text-red-700 uppercase tracking-wide">Afianzado Vencido</Text>
            <p className="text-lg font-bold text-red-700">{formatMonto(totals.montoVencido)}</p>
            {totals.fianzasVencidas > 0 && (
              <Text className="text-[10px] text-red-700/80 mt-0.5">
                requiere cancelación
              </Text>
            )}
          </div>
          <div className="bg-azul-surelia/5 border border-azul-surelia/15 rounded-lg p-3">
            <Text className="text-xs text-gray-500 uppercase tracking-wide">Contratos</Text>
            <p className="text-lg font-bold text-gray-900">{totals.totalContratos}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-zinc-700">{totals.fianzasVigentes} fianzas vigentes</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-zinc-700">{totals.fianzasPorEmitir} por emitir</span>
          </span>
          {totals.fianzasFuturas > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-zinc-300" />
              <span className="text-zinc-700" title="Sólo aplican al terminar el contrato (vicios ocultos).">
                {totals.fianzasFuturas} futuras
              </span>
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-zinc-700">{totals.fianzasVencidas} vencidas (cancelar)</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-zinc-400" />
            <span className="text-zinc-700">{totals.totalProveedores} proveedores</span>
          </span>
        </div>
      </SectionCard>

      {/* 1b. Ciclo de vida de las fianzas emitidas */}
      <SectionCard icon={ArrowPathIcon} title="Ciclo de Vida de las Fianzas">
        {lifecycle.total === 0 ? (
          <Text className="text-sm text-gray-500">No hay fianzas emitidas registradas.</Text>
        ) : (
          <>
            <div className="w-full h-2.5 rounded-full overflow-hidden flex bg-gray-100 mb-4">
              {LIFECYCLE_ESTADOS.map((e) => {
                const { count } = lifecycle.acc[e.code];
                if (!count) return null;
                return (
                  <div
                    key={e.code}
                    className={e.dot}
                    style={{ width: `${(count / lifecycle.total) * 100}%` }}
                    title={`${e.label}: ${count}`}
                  />
                );
              })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5">
              {LIFECYCLE_ESTADOS.map((e) => {
                const { count, monto } = lifecycle.acc[e.code];
                return (
                  <div key={e.code} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${e.dot}`} />
                    <div className="min-w-0">
                      <Text className="text-sm text-zinc-800">
                        <span className={`font-semibold ${e.alert && count > 0 ? 'text-red-700' : ''}`}>
                          {count}
                        </span>{' '}
                        {e.label.toLowerCase()}
                        <span className="text-xs text-zinc-400"> · {e.sub}</span>
                      </Text>
                      {count > 0 && monto > 0 && (
                        <Text className="text-[11px] text-zinc-500">{formatMonto(monto)}</Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </SectionCard>

      {/* 2. Próximos Vencimientos */}
      <SectionCard icon={ClockIcon} title="Próximos Vencimientos">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-red-600">≤ 30 días</Text>
            <p className="text-2xl font-bold text-red-700">{expBuckets.d30}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-amber-600">31–60 días</Text>
            <p className="text-2xl font-bold text-amber-700">{expBuckets.d60}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-yellow-700">61–90 días</Text>
            <p className="text-2xl font-bold text-yellow-700">{expBuckets.d90}</p>
          </div>
        </div>
        {upcomingExpirations.length === 0 ? (
          <Text className="text-sm text-gray-500">No hay fianzas por vencer en los próximos 90 días.</Text>
        ) : (
          <ul className="divide-y divide-gray-100">
            {upcomingExpirations.slice(0, 8).map((f) => {
              const days = Math.max(0, Math.floor((f._finMs - now) / 86400000));
              const toneClass = days <= 30
                ? 'bg-red-100 text-red-700'
                : days <= 60
                ? 'bg-amber-100 text-amber-700'
                : 'bg-yellow-100 text-yellow-700';
              return (
                <li
                  key={f.id || `${f.contractId}-${f._finMs}`}
                  onClick={() => onFianzaClick && onFianzaClick(f)}
                  className={`py-2 flex items-center gap-3 -mx-2 px-2 rounded ${onFianzaClick ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
                >
                  <span className={`min-w-[64px] text-center px-2 py-1 rounded text-xs font-semibold ${toneClass}`}>
                    {days}d
                  </span>
                  <div className="min-w-0 flex-1">
                    <Text className="text-sm font-medium text-zinc-800 truncate">
                      {f.contractNumero || '-'}
                    </Text>
                    <Text className="text-xs text-zinc-500 truncate">
                      {f.tipoFianza || f.tipo || 'Fianza'} · {f.proyectoNombre || 'Sin proyecto'}
                    </Text>
                  </div>
                  <span className="text-sm font-semibold text-azul-surelia whitespace-nowrap">
                    {formatMonto(f.montoAfianzado)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {/* 3. Distribución por Afianzadora */}
      <SectionCard icon={ChartBarIcon} title="Distribución por Afianzadora">
        {afianzadoraDistribution.length === 0 ? (
          <Text className="text-sm text-gray-500">No hay fianzas vigentes registradas.</Text>
        ) : (
          <ul className="space-y-3">
            {afianzadoraDistribution.map((af) => {
              const pct = montoVigenteTotal > 0 ? (af.monto / montoVigenteTotal) * 100 : 0;
              return (
                <li key={af.nombre}>
                  <div className="flex items-center gap-3 mb-1">
                    {af.logo ? (
                      <img src={af.logo} alt={af.nombre} className="w-8 h-8 rounded-lg object-contain bg-white border border-zinc-100 p-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-xs flex-shrink-0">
                        {af.nombre.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <Text className="text-sm font-medium text-zinc-800 truncate">{af.nombre}</Text>
                      <Text className="text-xs text-zinc-500">
                        {af.count} fianza{af.count !== 1 ? 's' : ''} · {formatMonto(af.monto)} · {pct.toFixed(1)}%
                      </Text>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-azul-surelia rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {/* 4. Top Proveedores con Atención Requerida */}
      <SectionCard icon={ExclamationTriangleIcon} title="Proveedores con Atención Requerida">
        {topProveedores.length === 0 ? (
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircleIcon className="w-5 h-5" />
            <Text className="text-sm">Todos los proveedores están al día.</Text>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {topProveedores.map((p) => {
              const exposureMonto = p.montos.pendiente + p.montos.vencida;
              return (
                <li
                  key={p.key}
                  onClick={() => onProveedorClick && onProveedorClick(p.key)}
                  className={`py-3 flex items-center gap-3 -mx-2 px-2 rounded ${onProveedorClick ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-sm flex-shrink-0">
                    {(p.label || '?').charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Text className="text-sm font-medium text-zinc-800 truncate">{p.label}</Text>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      {p.counts.porEmitir > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                          {p.counts.porEmitir} por emitir
                        </span>
                      )}
                      {p.counts.futura > 0 && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-600"
                          title="Sólo aplican al terminar el contrato (vicios ocultos)."
                        >
                          {p.counts.futura} futura{p.counts.futura !== 1 ? 's' : ''}
                        </span>
                      )}
                      {p.counts.overdue > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-200 text-amber-900">
                          {p.counts.overdue} atrasada{p.counts.overdue !== 1 ? 's' : ''}
                        </span>
                      )}
                      {p.counts.vencida > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                          {p.counts.vencida} vencida{p.counts.vencida !== 1 ? 's' : ''} (cancelar)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <Text className="text-sm font-semibold text-azul-surelia">{formatMonto(exposureMonto)}</Text>
                    <Text className="text-[10px] text-zinc-500">exposición</Text>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
};

export default ConvenioResumen;
